/**
 * PRA-001. Sign-in for practitioners and clinic staff, reached from the site footer: an emailed
 * code or a passkey, coming back to /clinic or to the invitation they were opening.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Banner } from "@lattice/ui/Banner";
import { Button } from "@lattice/ui/Button";
import { LinkButton } from "@lattice/ui/LinkButton";
import { signedIn, signOut } from "../api.ts";
import { EmailCodeSignIn } from "../EmailCodeSignIn.tsx";
import type { PasskeyOutcome } from "../passkey.ts";
import { registerPasskey, signInWithPasskey } from "../passkey.ts";
import { ClinicShell } from "./Shell.tsx";
const PASSKEY_MESSAGE: Readonly<Record<Exclude<PasskeyOutcome, "ok">, string>> = {
  unsupported: "This browser cannot use a passkey.",
  refused: "Passkeys are for clinic and staff accounts.",
  cancelled: "Passkey sign-in was cancelled.",
  failed: "The passkey could not be used. Use the emailed code."
};
export function ClinicSignIn({
  callbackURL = "/clinic"
}: {
  readonly callbackURL?: string;
}) {
  const [session, setSession] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    void signedIn().then(setSession);
  }, []);
  async function withPasskey(run: () => Promise<PasskeyOutcome>, onOk: () => void) {
    setPasskeyBusy(true);
    setPasskeyError(null);
    const outcome = await run();
    setPasskeyBusy(false);
    if (outcome === "ok") onOk();else setPasskeyError(PASSKEY_MESSAGE[outcome]);
  }
  if (session) return <ClinicShell title="You're signed in" description="Add a passkey on this device to skip the emailed code next time. The code still works.">
        <div className="x78zum5 xdt5ytf x1r0x45p x1qjc9v5">
          {passkeyError === null ? null : <Banner tone="danger" title="No passkey saved">
              {passkeyError}
            </Banner>}
          <LinkButton href="/clinic">Go to the clinic</LinkButton>
          <Button tone="quiet" corners="rounded" busy={passkeyBusy} onClick={() => void withPasskey(registerPasskey, () => setSaved(true))}>
            {saved ? "Passkey saved" : "Add a passkey"}
          </Button>
          <Button tone="quiet" corners="rounded" onClick={() => {
        void signOut().then(() => {
          setSession(false);
          setSaved(false);
        });
      }}>
            Sign out
          </Button>
        </div>
      </ClinicShell>;
  return <ClinicShell title="Clinic sign-in" description="We'll email you a six-digit code, or you can use a passkey. No password.">
      <div className="x78zum5 xdt5ytf x1r0x45p x1qjc9v5">
        <EmailCodeSignIn next={callbackURL} />
        <p className="x1ghz6dp x2b8uid xl4dl4e x1jty25b">or</p>
        {passkeyError === null ? null : <Banner tone="danger" title="No passkey used">
            {passkeyError}
          </Banner>}
        <Button tone="quiet" corners="rounded" wide busy={passkeyBusy} onClick={() => void withPasskey(signInWithPasskey, () => {
        window.location.assign(callbackURL);
      })}>
          Use a passkey
        </Button>
      </div>
    </ClinicShell>;
}